/**
 * usage-tab.tsx — Tab "Mức sử dụng" 7/30/90 ngày: tin vào/ra theo ngày, khách + hội thoại mới,
 * AI (lượt trả lời tính hạn mức — ai_usage_daily; lượt gọi + chi phí — ai_usage).
 */
import { useState } from 'react'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatCard } from '@/components/shared/stat-card'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { formatNumber } from '@/lib/utils'
import { useOrgUsage } from '@/hooks/use-platform-org-manage'

const axisStyle = { fontSize: 12, fill: 'hsl(var(--muted-foreground))' } as const
const gridStroke = 'hsl(var(--border))'
const tooltipStyle = {
  contentStyle: {
    background: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: 8,
    fontSize: 12,
    color: 'hsl(var(--popover-foreground))',
  },
  labelStyle: { color: 'hsl(var(--foreground))', fontWeight: 600 },
} as const
const shortDay = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`

export function UsageTab({ orgId }: { orgId: string }) {
  const [days, setDays] = useState(30)
  const { data, isLoading, isError, isFetching } = useOrgUsage(orgId, days)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((d) => (
          <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)}>{d} ngày</Button>
        ))}
        {isFetching && <span className="text-xs text-muted-foreground">Đang cập nhật…</span>}
      </div>
      {isLoading ? <Loading /> : isError || !data ? <ErrorState message="Không tải được mức sử dụng." /> : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard label="Tin nhận" value={formatNumber(data.totals.inbound)} />
            <StatCard label="Tin gửi" value={formatNumber(data.totals.outbound)} />
            <StatCard label="Khách mới" value={formatNumber(data.totals.newContacts)} />
            <StatCard label="Hội thoại mới" value={formatNumber(data.totals.newConversations)} />
            <StatCard label="Chi phí AI (USD)" value={`$${data.totals.aiCostUsd.toFixed(2)}`} hint={`${formatNumber(data.totals.aiCalls)} lượt gọi`} />
          </div>

          <Card>
            <CardHeader><CardTitle>Tin nhắn theo ngày</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.series} margin={{ left: -16, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="day" tickFormatter={shortDay} tick={axisStyle} tickLine={false} axisLine={false} minTickGap={16} />
                  <YAxis tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(d: string) => shortDay(d)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="inbound" name="Nhận" stroke="hsl(var(--success))" fill="hsl(var(--success) / 0.15)" strokeWidth={2} />
                  <Area type="monotone" dataKey="outbound" name="Gửi" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.15)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Khách & hội thoại mới</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.series} margin={{ left: -16, right: 8, top: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="day" tickFormatter={shortDay} tick={axisStyle} tickLine={false} axisLine={false} minTickGap={16} />
                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip {...tooltipStyle} labelFormatter={(d: string) => shortDay(d)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="newContacts" name="Khách mới" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="newConversations" name="Hội thoại mới" fill="hsl(var(--success))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>AI theo ngày</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.series} margin={{ left: -16, right: 8, top: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="day" tickFormatter={shortDay} tick={axisStyle} tickLine={false} axisLine={false} minTickGap={16} />
                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      {...tooltipStyle}
                      labelFormatter={(d: string) => shortDay(d)}
                      formatter={(v: number, name: string) => [name === 'Chi phí (USD)' ? `$${v.toFixed(4)}` : formatNumber(v), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="aiReplies" name="Lượt tính hạn mức" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="aiCalls" name="Lượt gọi mô hình" fill="hsl(var(--warning))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Bảng chi tiết</CardTitle></CardHeader>
            <CardContent className="max-h-96 overflow-auto p-0">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    {['Ngày', 'Nhận', 'Gửi', 'Khách mới', 'Hội thoại mới', 'AI (hạn mức)', 'AI gọi', 'Token', 'Chi phí USD'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...data.series].reverse().map((r) => (
                    <tr key={r.day} className="border-b last:border-0">
                      <td className="px-3 py-1.5">{shortDay(r.day)}/{r.day.slice(0, 4)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.inbound)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.outbound)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.newContacts)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.newConversations)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.aiReplies)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.aiCalls)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatNumber(r.aiTokens)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.aiCostUsd ? r.aiCostUsd.toFixed(4) : '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">Ngày tính theo giờ Việt Nam. Dung lượng lưu trữ chưa thống kê (tệp đính kèm nằm ngoài DB).</p>
        </>
      )}
    </div>
  )
}
